import { useEffect, useState } from 'react';

import { Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { T } from 'react-translator-component';
import styled from 'styled-components';

import { getNotifications } from './helpers';

import { getChartsData } from '../../../../hooks/indicators';
import { getNotificationsByUserPk } from '../../../../hooks/notifications';
import { getDeposits, getDepositsTotalizers, getUserDashboardDetail } from '../../../../hooks/users';
import { addFilteredUserNotifications } from '../../../../redux/actions';
import { INotifications } from '../../../../types/responses/Notifications';
import { UserDashboardDetailProps } from '../../../../types/responses/UsersDashboard';
import { isMonetizationProUser } from '../../../../utils/isMonetizationProUser/isMonetizationProUser';
import { isTerpelTenant } from '../../../../utils/isTerpelTenant/isTerpelTenant';
import { COLORS } from '../../../../utils/theme/colors';
import { ChargerPlugIcon } from '../../../atoms/icons/ChargerPlugIcon';
import { ThunderIcon } from '../../../atoms/icons/ThunderIcon';
import { BlackChart } from '../../../molecules/charts/BlackChart';
import {
  getColorByTitle,
  MOCK_1,
  parseChartData,
} from '../../../molecules/charts/tools';
import RealTimelineCardStyle from '../../RealTimelineCardStyle/RealTimelineCardStyle';
import UserWallet from '../../UserWallet/UserWallet';
import { UsersInnerData } from '../listFormater';

interface IUsersCardInnerContent {
  index: number;
  data: any;
  className: string;
  selected: boolean;
}

export const UsersCardInnerContent = ({
  className,
  index,
  data,
  selected,
}: IUsersCardInnerContent) => {
  const userSelected: UsersInnerData = data[index];

  const notificationsResponse: INotifications[] = useSelector(
    (state: any) =>
      state?.notifications?.filteredUserNotifications ?? undefined,
  );
  const dispatch = useDispatch();

  const userNotifications = getNotifications(
    notificationsResponse,
    userSelected,
  );

  const [chartsData, setchartsData] = useState(MOCK_1);
  const [chartsLoading, setchartsLoading] = useState(true);
  const [deposits, setDeposits] = useState([]);
  const [depositsTotalizer, setDepositsTotalizer] = useState(null);
  const [userWalletLoading, setUserWalletLoading] = useState(false);
  const [dashboardDetail, setDashboardDetail] = useState<UserDashboardDetailProps | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    (async () => {
      if (selected && userSelected?.user_pk) {
        try {
          setDetailLoading(true);
          const response = await getUserDashboardDetail(userSelected.user_pk);
          if (response?.userDashboardDetail) {
            setDashboardDetail(response.userDashboardDetail);
          }
        } finally {
          setDetailLoading(false);
        }
      }
    })();
  }, [selected, userSelected?.user_pk]);

  useEffect(() => {
    (async function () {
      if (chartsData[1].mock) {
        setchartsData(
          parseChartData(
            await getChartsData('28', '', String(userSelected.user_pk)),
            '28',
          ),
        );
        setchartsLoading(false);
      }
    })();
  }, [chartsData, userSelected.user_pk]);

  useEffect(() => {
    (async () => {
      userSelected &&
        getNotificationsByUserPk(userSelected.user_pk).then((data) =>
          dispatch(addFilteredUserNotifications(data, userSelected.user_pk)),
        );
    })();
  }, [dispatch, userSelected]);

  const loadWallet = async () => {
    if (selected && userSelected) {
      try {
        setUserWalletLoading(true);

        const depositsData = await getDeposits(userSelected.user_pk);

        if (depositsData) {
          setDeposits(depositsData.deposits);
        }

        const depositsTotalizer = await getDepositsTotalizers(
          userSelected.user_pk,
        );

        if (depositsTotalizer) {
          setDepositsTotalizer(depositsTotalizer);
        }
      } finally {
        setUserWalletLoading(false);
      }
    }
  };

  useEffect(() => {
    loadWallet();
  }, [selected, userSelected]);

  return (
    <Div className={className}>
      <div className='section'>
        <div className='station_details'>
          <p className='strong'>ID Tag</p>
          <p className='detail_value'>
            {userSelected?.ocpp_id_tag || 'ID Tag não cadastrada'}
          </p>

          {!userSelected.is_vehicle && (
            <>
              <p className='strong'>E-mail</p>
              <p className='detail_value'>
                {(!!userSelected?.email?.trim() && userSelected?.email) ||
                  'E-mail não cadastrado'}
              </p>
            </>
          )}

          {detailLoading ? (
            <Spin size='small' style={{ gridColumn: '1 / -1', justifySelf: 'start', marginTop: 4 }} />
          ) : dashboardDetail ? (
            <>
              <p className='strong'>{T('Recharges Done')}</p>
              <p className='detail_value stat_with_icon'>
                <ChargerPlugIcon size={16} />
                {dashboardDetail.recharges ?? 0}
              </p>

              <p className='strong'>{T('kWh Consumed')}</p>
              <p className='detail_value stat_with_icon'>
                <ThunderIcon size={16} />
                {dashboardDetail.consumption
                  ? (dashboardDetail.consumption / 1000).toFixed(2)
                  : '0.00'}
              </p>

              <p className='strong'>Status</p>
              <p className={`detail_value charging_status ${dashboardDetail.chargingStatus === 'Charging' ? 'is_charging' : 'not_charging'}`}>
                {dashboardDetail.chargingStatus === 'Charging'
                  ? T('Charging')
                  : T('Not Charging')}
              </p>
            </>
          ) : null}
        </div>
        <Link
          className='user_details cursor'
          to={{
            pathname: userSelected?.is_vehicle
              ? `/vehicle/${userSelected?.user_pk}/edit`
              : `/user/${userSelected?.user_pk}`,
            state: { isVehicle: userSelected?.is_vehicle },
          }}
        >
          <p>{userSelected.is_vehicle ? T('Edit Vehicle') : T('Edit User')}</p>
        </Link>
      </div>
      <RealTimelineCardStyle data={userNotifications} />
      <BlackChart
        {...{
          key: chartsData[1]?.key,
          data: chartsData[1]?.series,
          className: 'energy_chart',
          title: chartsData[1]?.title,
          tooltip: chartsData[1]?.tooltip,
          loading: chartsLoading,
          color: getColorByTitle(chartsData[1]?.key),
        }}
      />

      {(isMonetizationProUser() || isTerpelTenant()) &&
        !userSelected.is_vehicle && (
          <UserWallet
            loading={userWalletLoading}
            deposits={deposits}
            depositsTotalizer={depositsTotalizer}
            userPk={userSelected.user_pk}
            onAdjusted={loadWallet}
          />
        )}
    </Div>
  );
};

const Div = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 20px;

  .station_details {
    padding-left: 20px;
    margin-top: 20px;
    width: 90%;
    max-width: 320px;
    display: grid;
    grid-template-columns: auto 1fr;
    grid-auto-rows: 26px;
    align-items: center;

    p {
      margin: 0;
      font-size: 12px;
      white-space: nowrap;
    }

    .strong {
      font-weight: 600;
      padding-right: 12px;
    }

    .detail_value {
      color: #555;
    }

    .stat_with_icon {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .charging_status {
      font-weight: 600;
      font-size: 11px;
      padding: 2px 8px;
      border-radius: 4px;
      width: fit-content;
    }

    .is_charging {
      color: #389e0d;
      background-color: #f6ffed;
    }

    .not_charging {
      color: #888;
      background-color: #f5f5f5;
    }
  }

  .chart_builder_wrapper {
    background-color: #fff;
    box-shadow: 0 0 10px 1px rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    height: max-content;
    padding: 15px 15px 10px;
  }
  .user_details {
    margin: 20px 60px;
    padding: 10px 40px;
    border-radius: 8px;
    width: 170px;
    height: 55px;
    display: flex;
    flex-direction: row;
    text-decoration: none;
    align-items: center;
    justify-content: space-between;
    font-size: 13px;
    transition: background-color 200ms ease-in-out;
    background-color: rgba(0, 0, 0, 0.05);
    color: #555;

    p {
      margin: 0;
    }

    :hover path {
      fill: ${COLORS.primary};
    }

    :hover {
      background-color: ${({ theme }) => theme.hoveredDiv};
      color: ${COLORS.primary};
    }
  }
  .chart {
    background-color: #fff;
    box-shadow: 0 0 10px 0px rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    padding: 20px 20px 100px 45px;
    max-width: 350px;
    width: 27%;
    height: calc(100% - 20px);
    opacity: 0;
  }
`;
